import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import { PoweredByComponent } from '@ui/common';
import { CookiesInfoComponent } from '@ui/features';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { OwnerPathInterceptor, SUBDOMAIN_TOKEN } from 'angular-v2-services';
import { GlobalErrorHandler } from 'angular-v2-utils';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ToastrModule } from 'ngx-toastr';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ConciergeWidgetComponent } from './components/shared/concierge-widget/concierge-widget.component';
import { ApiPrefixInterceptor } from './interceptors/api-prefix-interceptor';
import { ErrorHandlerInterceptor } from './interceptors/error-handler.interceptor';
import { GuestIdInterceptor } from './interceptors/guest-id.interceptor';
import { VisitorsAreaService } from './services/visitors-area.service';
import { companyType } from './utils/company-type';
import { initializeTranslations } from './utils/initialize-translation';
import { createTranslateLoaderFromApi } from './utils/translate-loader';
import { NotFoundComponent } from './views/shared-views/not-found/not-found.component';

const imports = [
  BrowserModule,
  BrowserAnimationsModule,
  AppRoutingModule,
  ToastrModule.forRoot({
    positionClass: 'toast-top',
    closeButton: false,
  }),
  ModalModule.forRoot(),
  TranslateModule.forRoot({
    loader: {
      provide: TranslateLoader,
      useFactory: createTranslateLoaderFromApi,
      deps: [HttpClient],
    },
    extend: true,
    isolate: false,
  }),
  AngularSvgIconModule.forRoot(),
];

if (environment.secured) {
  imports.push(
    JwtModule.forRoot({
      config: {
        tokenGetter: () => {
          return localStorage.getItem('guestId');
        },
        headerName: 'guestid',
        authScheme: '',
        allowedDomains: environment?.jwt?.allowedDomains ?? [],
      },
    }),
  );
}

if (environment?.gtmId) {
  imports.push(
    GoogleTagManagerModule.forRoot({
      id: environment.gtmId,
    }),
  );
}

const providers: Provider[] = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: GuestIdInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: OwnerPathInterceptor,
    multi: true,
  },
  {
    provide: APP_INITIALIZER,
    useFactory: initializeTranslations,
    deps: [TranslateService],
    multi: true,
  },
  {
    provide: APP_INITIALIZER,
    useFactory: companyType,
    deps: [VisitorsAreaService],
    multi: true,
  },
  {
    provide: ErrorHandler,
    useClass: GlobalErrorHandler,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorHandlerInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ApiPrefixInterceptor,
    multi: true,
  },
  { provide: SUBDOMAIN_TOKEN, useValue: environment.demoGA },
];

if (environment.sentryDns) {
  providers.push(
    ...[
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler({
          showDialog: false,
        }),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      {
        provide: APP_INITIALIZER,
        useFactory: () => () => {},
        deps: [Sentry.TraceService],
        multi: true,
      },
    ],
  );
}

@NgModule({
  declarations: [AppComponent, NotFoundComponent],
  exports: [TranslateModule],
  imports: [imports, CookiesInfoComponent, PoweredByComponent, ConciergeWidgetComponent],
  providers: [...providers, provideHttpClient(withInterceptorsFromDi())],
  bootstrap: [AppComponent],
})
export class AppModule {}
